import { Avatar, Button, Divider, Flex, HStack, Link, Text, VStack } from '@chakra-ui/react'
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { AdminFeedbackModerationAction, AdminReportedFeedback, FeedbackModerationStatus, FeedbackReportStatus } from '../../services/network'
import { useTranslation } from 'react-i18next'
import { formatISODate } from '../../utils/utils'

interface IReportedFeedbackProps {
  feedback: AdminReportedFeedback
  isLoading: boolean
  pendingAction: AdminFeedbackModerationAction | undefined
  onKeep: () => void
  onRemove: () => void
  onCancel: () => void
}

function ReportedFeedback({ feedback, isLoading, pendingAction, onKeep, onRemove, onCancel }: IReportedFeedbackProps): JSX.Element {
  const { t } = useTranslation()
  const isPendingReview = feedback.reportStatus === FeedbackReportStatus.Reported
  const visibleModerationStatus = getVisibleModerationStatus()

  function getVisibleModerationStatus(): FeedbackModerationStatus | undefined {
    if (pendingAction === AdminFeedbackModerationAction.Keep) {
      return FeedbackModerationStatus.Kept
    }

    if (pendingAction === AdminFeedbackModerationAction.Remove) {
      return FeedbackModerationStatus.Removed
    }

    return feedback.moderationStatus
  }

  function getModerationStatusLabel(moderationStatus: FeedbackModerationStatus | undefined): string {
    switch (moderationStatus) {
      case FeedbackModerationStatus.Kept:
        return t('feedback_moderation_status_kept')
      case FeedbackModerationStatus.Removed:
        return t('feedback_moderation_status_removed')
      default:
        return ''
    }
  }

  function getModerationStatusVariant(moderationStatus: FeedbackModerationStatus | undefined): string {
    switch (moderationStatus) {
      case FeedbackModerationStatus.Kept:
        return 'Accepted'
      case FeedbackModerationStatus.Removed:
        return 'Refused'
      default:
        return ''
    }
  }

  return (
    <Flex marginBottom={'10px'} marginRight={'10px'} direction={'column'} justifyContent={'flex-start'}>
      <Flex gap={'24px'} alignItems={'flex-start'}>
        <Flex direction={'column'} width={'280px'} flexShrink={0} borderRadius={'12px'} bg={'NeutralLight'} padding={'16px'} gap={'12px'}>
          <HStack
            as={RouterLink}
            to={`/users/userinfo/${feedback.author.id}`}
            width={'100%'}
            spacing={'12px'}
            alignItems={'center'}
            _hover={{ textDecoration: 'none' }}>
            <Avatar name={feedback.author.username} src={feedback.author.profileImageUrl}></Avatar>
            <VStack spacing={'0px'} alignItems={'flex-start'} minWidth={0}>
              <Text fontSize='14px' fontWeight='700'>
                {t('feedback_author_user')}
              </Text>
              <Text fontSize='16px' fontWeight='600' noOfLines={1}>{`${feedback.author.firstName} ${feedback.author.lastName}`}</Text>
              <Text fontSize='14px' color={'greyCold300'} noOfLines={1}>{`@${feedback.author.username}`}</Text>
            </VStack>
          </HStack>
          <HStack
            as={RouterLink}
            to={`/users/userinfo/${feedback.receiver.id}`}
            width={'100%'}
            spacing={'12px'}
            alignItems={'center'}
            _hover={{ textDecoration: 'none' }}>
            <Avatar name={feedback.receiver.username} src={feedback.receiver.profileImageUrl}></Avatar>
            <VStack spacing={'0px'} alignItems={'flex-start'} minWidth={0}>
              <Text fontSize='14px' fontWeight='700'>
                {t('feedback_received_user')}
              </Text>
              <Text fontSize='16px' fontWeight='600' noOfLines={1}>{`${feedback.receiver.firstName} ${feedback.receiver.lastName}`}</Text>
              <Text fontSize='14px' color={'greyCold300'} noOfLines={1}>{`@${feedback.receiver.username}`}</Text>
            </VStack>
          </HStack>
        </Flex>

        <Flex direction={'column'} flex={'1'} minWidth={0} gap={'10px'}>
          <HStack spacing={'12px'} flexWrap={'wrap'}>
            <Link as={RouterLink} to={`/events/${feedback.eventId}`} fontSize='18px' fontWeight='700' color={'inherit'}>
              {feedback.eventName}
            </Link>
            <Text fontSize='14px' color={'greyCold300'}>
              {feedback.feedbackId}
            </Text>
          </HStack>
          <HStack spacing={'16px'} flexWrap={'wrap'}>
            <Text fontSize='14px'>
              <strong>{t('rating')}</strong>: {feedback.rating}/5
            </Text>
            <Text fontSize='14px'>
              <strong>{t('feedback_created_at')}</strong>: {formatISODate(feedback.createdAt)}
            </Text>
            <Text fontSize='14px'>
              <strong>{t('feedback_reported_at')}</strong>: {formatISODate(feedback.reportedAt)}
            </Text>
          </HStack>
          <Text fontSize='14px'>
            <strong>{t('feedback_role')}</strong>: {feedback.authorRole}
          </Text>
          <VStack spacing={'4px'} alignItems={'flex-start'}>
            <Text fontSize='14px' fontWeight='700'>
              {t('review_label')}
            </Text>
            <Text fontSize='14px' color={'greyCold300'} overflowWrap={'anywhere'} wordBreak={'break-word'} whiteSpace={'pre-wrap'}>
              {feedback.text}
            </Text>
          </VStack>
          <VStack spacing={'4px'} alignItems={'flex-start'}>
            <Text fontSize='14px' fontWeight='700'>
              {t('feedback_report_reason')}
            </Text>
            <Text fontSize='14px' color={'greyCold300'} overflowWrap={'anywhere'} wordBreak={'break-word'} whiteSpace={'pre-wrap'}>
              {feedback.reportReason}
            </Text>
          </VStack>
        </Flex>

        <Flex gap={'10px'} alignItems={'center'} justifyContent={'flex-end'} minWidth={'150px'}>
          {isPendingReview && !pendingAction ? (
            <>
              <Button onClick={onRemove} borderRadius={'17px'} width={'100%'} colorScheme='red' isLoading={isLoading} isDisabled={isLoading}>
                {t('feedback_remove')}
              </Button>
              <Button
                onClick={onKeep}
                borderRadius={'17px'}
                width={'100%'}
                variant={'outline'}
                color={'primary.400'}
                borderColor={'primary.400'}
                isLoading={isLoading}
                isDisabled={isLoading}>
                {t('feedback_keep')}
              </Button>
            </>
          ) : isPendingReview && pendingAction ? (
            <>
              <Button onClick={onCancel} isDisabled={isLoading}>
                <Text fontSize='12px' fontWeight='600' lineHeight='16.8px' color={'#54636F'}>
                  {t('cancel')}
                </Text>
              </Button>
              <Text variant={getModerationStatusVariant(visibleModerationStatus)} alignContent={'center'} height={'22px'}>
                {getModerationStatusLabel(visibleModerationStatus)}
              </Text>
            </>
          ) : (
            <Text variant={getModerationStatusVariant(visibleModerationStatus)} alignContent={'center'} height={'22px'}>
              {getModerationStatusLabel(visibleModerationStatus)}
            </Text>
          )}
        </Flex>
      </Flex>
      <Divider orientation='horizontal' marginTop={'16px'} />
    </Flex>
  )
}

export { ReportedFeedback }
